import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { altaCosturaTestimonials } from '../../../mocks/testimonios';
import { getAdminData } from '../../../hooks/useAdminData';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  text: string;
  rating: number;
  type: 'alta-costura' | 'arreglos';
  date: string;
  service: string;
}

export default function AltaCosturaTestimonials() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.05, distance: 36, duration: 900 });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const testimonialList = getAdminData<Testimonial[]>('admin_alta_costura_testimonials', altaCosturaTestimonials);

  return (
    <section ref={ref} className="w-full bg-white py-20 md:py-28">
      <div className="px-6 lg:px-[7%]">
        <div className="text-center max-w-2xl mx-auto mb-14" style={style}>
          <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.2em] uppercase font-medium">
            Alta Costura
          </span>
          <h2 className="font-serif text-brand-black text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.15] mt-4">
            Experiencias de <span className="italic text-brand-gray-300">lujo artesanal</span>
          </h2>
          <p className="font-sans text-brand-gray-500 text-base leading-[1.8] mt-5">
            Clientas que viajaron desde Madrid, Milán, París y Caracas para vivir la
            experiencia completa de alta costura en Barcelona.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {testimonialList.map((t, index) => (
            <div
              key={t.id}
              className="bg-brand-gray-50 rounded-3xl p-8 md:p-10"
              style={isVisible ? {
                opacity: 1,
                transform: 'translateY(0px)',
                transition: `opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 120}ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 120}ms`,
              } : {
                opacity: 0,
                transform: 'translateY(36px)',
                transition: `opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 120}ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 120}ms`,
                willChange: 'opacity, transform',
              }}
            >
              <div className="flex items-center gap-1 mb-6">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-brand-black text-sm" />
                ))}
              </div>

              <p className="font-serif text-brand-gray-600 text-lg leading-[1.7] italic">
                &ldquo;{expandedId === t.id ? t.text : `${t.text.slice(0, 180)}...`}&rdquo;
              </p>

              {t.text.length > 180 && (
                <button
                  onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                  className="font-sans text-brand-black text-xs tracking-wider uppercase mt-3 hover:text-brand-gray-500 transition-colors"
                >
                  {expandedId === t.id ? 'Leer menos' : 'Leer más'}
                </button>
              )}

              <div className="mt-8 pt-6 border-t border-brand-gray-200 flex items-center justify-between">
                <div>
                  <p className="font-sans text-brand-black text-sm font-bold">
                    {t.name}
                  </p>
                  <p className="font-sans text-brand-gray-400 text-xs mt-0.5">
                    {t.location}
                  </p>
                </div>
                <span className="font-sans text-brand-gray-400 text-[11px] tracking-wider uppercase bg-white px-3 py-1.5 rounded-full">
                  {t.service}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}